import React, { useEffect, useRef, useState } from 'react'

import ChatService from '../../services/ChatService'

import './Chat.scss'

const ChatRoom = ({ thread, currentUser, onBack }) => {
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    loadMessages()

    const handleNewMessage = e => {
      const msg = e.detail
      if (msg.chatThreadId === thread.id) {
        setMessages(prev => {
          // Avoid duplicates
          if (prev.find(m => m.id === msg.id)) return prev
          return [...prev, msg]
        })

        // Mark read and trigger global refresh
        // Don't await - optimistic
        ChatService.markRead(thread.id).then(() => {
          window.dispatchEvent(new Event('chat:refresh-unread'))
        })
      }
    }

    window.addEventListener('chat-message', handleNewMessage)
    return () => window.removeEventListener('chat-message', handleNewMessage)
  }, [thread.id])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const loadMessages = async () => {
    try {
      const data = await ChatService.getMessages(thread.id)
      setMessages(Array.isArray(data) ? data : [])
      setLoading(false)
      // Focus input on load
      setTimeout(() => inputRef.current?.focus(), 100)
    } catch (err) {
      console.error('Failed to load messages', err)
      setLoading(false)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSend = async e => {
    e.preventDefault()
    if (!newMessage.trim()) return

    try {
      const sentMessage = await ChatService.sendMessage(thread.id, newMessage)
      setMessages([...messages, sentMessage])
      setNewMessage('')
    } catch (err) {
      console.error('Failed to send message', err)
      // Fallback: alert the user if UI doesn't have error state yet
      alert(`Failed to send message: ${err.message}`)
    }
  }

  const getRecipientName = t => {
    if (!t.participants || t.participants.length === 0) return 'Unknown'
    const other = t.participants.find(p => p.userId !== currentUser?.id)
    return other ? other.fullName : 'Me'
  }

  return (
    <div className="chat-room">
      <div className="chat-header align-items-center">
        {onBack && (
          <button className="btn btn-link btn-sm me-2 text-dark" onClick={onBack}>
            <i className="fas fa-arrow-left"></i>
          </button>
        )}
        <div style={{ overflow: 'hidden' }}>
          {/* Recipient Name */}
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            With: {getRecipientName(thread)}
          </div>
          {/* Subject */}
          <h5
            className="mb-0 text-truncate text-dark font-weight-bold"
            title={thread.subject}
            style={{ fontSize: '1rem' }}
          >
            {thread.subject || 'No Subject'}
          </h5>
        </div>
      </div>

      <div className="chat-messages">
        {loading && messages.length === 0 ? (
          <div className="text-center p-3">Loading...</div>
        ) : (
          messages.map(msg => {
            const isMe = msg.senderUserId === currentUser?.id
            return (
              <div key={msg.id} className={`message-bubble ${isMe ? 'me' : 'them'}`}>
                <div className="message-content">{msg.content}</div>
                <div className="message-time">
                  {new Date(msg.sentOn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            )
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-area" onSubmit={handleSend}>
        <div className="input-group">
          <input
            ref={inputRef}
            type="text"
            className="form-control mr-2"
            placeholder="Type a message..."
            value={newMessage}
            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
            onChange={e => setNewMessage(e.target.value)}
          />
          <button className="btn btn-primary" type="submit" disabled={!newMessage.trim()}>
            <i className="fas fa-paper-plane"></i>
          </button>
        </div>
      </form>
    </div>
  )
}

export default ChatRoom
